import React, { useMemo, useState } from 'react';
import CheckIcon from '@material-ui/icons/Check';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import TextField from 'Components/Atoms/TextField';
import Select from 'Components/Atoms/Select';
import SwitchBox from 'Components/Atoms/Switch';
import InputAdornmentAtom from 'Components/Atoms/InputAdornment';
import { Event, TicketCategory } from 'types/shared/documents/restaurants/event';
import {
  hasTicketCategories,
  normalizeTicketCategoryEvent,
  ticketCategoryErrors,
} from 'types/shared/helper/ticketCategories';
import { toCurrencyString } from 'utils/helper';
import { toVatCents, vatRateLabel } from 'types/shared/helper/eventVatComponents';
import useRestaurantVatCountry from 'CustomHooks/useRestaurantVatCountry';
import useRestaurant from 'CustomHooks/useRestaurant';
import VatSplitEditor from './VatSplitEditor';
import './ticket-categories.scss';

export type ShiftOccassion = { id: string; title: string };

type Sale = 'ticket' | 'paused' | 'without';

type Props = {
  eventState: Event;
  setEventState: React.Dispatch<React.SetStateAction<Event | null>>;
  /** Occasions of the shift the event belongs to, in display order. */
  shiftOccassions: ShiftOccassion[];
  /** False when the restaurant has not unlocked editing (existing configuration stays visible). */
  editable: boolean;
};

const newCategoryId = (occassion: string) =>
  `${occassion}-${Math.random().toString(36).slice(2, 8)}`;

const SALE_OPTIONS = [
  { id: 'ticket', label: 'Mit Ticket' },
  { id: 'paused', label: 'Pausiert' },
  { id: 'without', label: 'Ohne Ticket' },
];

/** Price range of the active categories, e.g. "49,00 € – 149,00 €". */
export function ticketCategoryPriceRange(event: Event | null | undefined): string | null {
  if (!hasTicketCategories(event)) return null;
  const amounts = event!.ticketCategories!
    .filter((category) => category.active !== false)
    .map((category) => category.amount);
  if (!amounts.length) return null;
  const low = Math.min(...amounts);
  const high = Math.max(...amounts);
  return low === high
    ? toCurrencyString(low, '€', false, 1)
    : `${toCurrencyString(low, '€', false, 1)} – ${toCurrencyString(high, '€', false, 1)}`;
}

type Pickable = { id: string; internalName: string; translation?: { de?: { title?: string } } };

/** Compact on/off toggles for the options or add-ons a category sells. */
const PickGroup = ({
  label,
  items,
  selected,
  disabled,
  onToggle,
}: {
  label: string;
  items: Pickable[];
  selected: string[] | null | undefined;
  disabled: boolean;
  onToggle: (id: string, checked: boolean) => void;
}) => (
  <div className="ticket-category__picks" role="group" aria-label={label}>
    <span className="ticket-category__picks-label">{label}</span>
    {items.map((item) => {
      const checked = !selected || selected.includes(item.id);
      return (
        <button
          key={item.id}
          type="button"
          role="checkbox"
          aria-checked={checked}
          disabled={disabled}
          className={`ticket-category__pick${checked ? ' is-checked' : ''}`}
          onClick={() => onToggle(item.id, !checked)}
        >
          {checked && <CheckIcon aria-hidden className="ticket-category__pick-icon" />}
          {item.translation?.de?.title || item.internalName}
        </button>
      );
    })}
  </div>
);

const TicketCategoriesEditor = ({ eventState, setEventState, shiftOccassions, editable }: Props) => {
  const enabled = hasTicketCategories(eventState);
  const excluded = eventState.rules?.excludeOccassions ?? [];
  const errors = useMemo(
    () => ticketCategoryErrors(eventState, shiftOccassions.map((occassion) => occassion.id)),
    [eventState, shiftOccassions]
  );
  const priceRange = ticketCategoryPriceRange(eventState);

  const update = (change: (event: Event) => Event) =>
    setEventState((current) => (current ? normalizeTicketCategoryEvent(change(current)) : current));

  const setCategories = (event: Event, categories: TicketCategory[]): Event => ({
    ...event,
    ticketCategories: categories,
  });

  const setExcluded = (event: Event, occassion: string, exclude: boolean): Event => {
    const current = event.rules?.excludeOccassions ?? [];
    const excludeOccassions = exclude
      ? Array.from(new Set([...current, occassion]))
      : current.filter((id) => id !== occassion);
    return { ...event, rules: { ...(event.rules ?? {}), excludeOccassions } };
  };

  const toggle = () =>
    update((event) => {
      if (hasTicketCategories(event)) {
        const { ticketCategories, ...withoutCategories } = event;
        return withoutCategories as Event;
      }
      return setCategories(
        event,
        shiftOccassions
          .filter((occassion) => !excluded.includes(occassion.id))
          .map((occassion) => ({
            id: newCategoryId(occassion.id),
            occassion: occassion.id,
            amount: event.amount ?? 0,
          }))
      );
    });

  const changeCategory = (occassion: string, change: Partial<TicketCategory>) =>
    update((event) =>
      setCategories(
        event,
        (event.ticketCategories ?? []).map((category) =>
          category.occassion === occassion ? { ...category, ...change } : category
        )
      )
    );

  const changeSale = (occassion: string, sale: Sale) =>
    update((event) => {
      const categories = event.ticketCategories ?? [];
      const existing = categories.find((category) => category.occassion === occassion);
      if (sale === 'without') {
        return setExcluded(
          setCategories(event, categories.filter((category) => category.occassion !== occassion)),
          occassion,
          true
        );
      }
      const active = sale === 'ticket';
      const next = existing
        ? categories.map((category) => (category.occassion === occassion ? { ...category, active } : category))
        : [...categories, { id: newCategoryId(occassion), occassion, amount: event.amount ?? 0, active }];
      return setExcluded(setCategories(event, next), occassion, false);
    });

  const pickIds = (
    all: { id: string }[],
    current: string[] | null | undefined,
    id: string,
    checked: boolean
  ): string[] | null => {
    const selected = new Set(current ?? all.map((item) => item.id));
    if (checked) selected.add(id);
    else selected.delete(id);
    const ids = all.map((item) => item.id).filter((itemId) => selected.has(itemId));
    return ids.length === all.length ? null : ids;
  };

  const options = eventState.options ?? [];
  const addOns = eventState.optionalAddOns ?? [];
  const vatCountry = useRestaurantVatCountry();
  const { restaurantId } = useRestaurant();
  // Occasion whose tax split is open for editing.
  const [openVat, setOpenVat] = useState<string | null>(null);

  /** Rate the category price falls under without a split: that of its first option. */
  const defaultRate = (category: TicketCategory) =>
    options.find((option) => !category.optionIds || category.optionIds.includes(option.id))?.vatRate ?? '19.00';

  const vatSummary = (category: TicketCategory) =>
    category.vatComponents?.length
      ? category.vatComponents
          .map(
            (component) =>
              `${component.title} ${toCurrencyString(component.grossAmountCents / 100, '€', false, 1)} (${vatRateLabel(component.vatRate)})`
          )
          .join(' · ')
      : `Ein Satz: ${vatRateLabel(String(defaultRate(category)))} (aus der Option)`;

  return (
    <section className="ticket-categories" aria-labelledby="ticket-categories-title">
      <div className="ticket-categories__intro">
        <div>
          <Typography id="ticket-categories-title" variant="h5" block>
            Unterschiedliche Preise je Anlass
          </Typography>
          <Typography color="subdued" variant="text-3" block className="ticket-categories__lede">
            Ein Abend, eine Schicht: Jeder Anlass bekommt seinen eigenen Ticketpreis und auf Wunsch
            eigene Optionen.
          </Typography>
          {shiftOccassions.length < 2 && !enabled && (
            <Typography color="subdued" variant="text-4" block>
              Dafür braucht die Schicht mindestens zwei Anlässe.
            </Typography>
          )}
          {!editable && (
            <Typography color="subdued" variant="text-4" block>
              Preiskategorien sind für dieses Restaurant nicht freigeschaltet.
            </Typography>
          )}
        </div>
        <SwitchBox
          checked={enabled}
          disabled={!editable || (!enabled && shiftOccassions.length < 2)}
          onChange={toggle}
          label=""
        />
      </div>

      {enabled && (
        <>
          <ul className="ticket-categories__list">
            {shiftOccassions.map((occassion) => {
              const category = eventState.ticketCategories?.find((c) => c.occassion === occassion.id);
              const sale: Sale = excluded.includes(occassion.id)
                ? 'without'
                : category && category.active !== false
                  ? 'ticket'
                  : 'paused';
              const showPicks = !!category && sale !== 'without' && (options.length > 1 || addOns.length > 0);
              return (
                <li key={occassion.id} className={`ticket-category is-${sale}`}>
                  <div className="ticket-category__name">
                    <Typography variant="text-2" weight="bold" block>
                      {occassion.title}
                    </Typography>
                    {sale === 'paused' && (
                      <Typography variant="text-4" color="subdued" block>
                        Gerade nicht buchbar
                      </Typography>
                    )}
                  </div>

                  <div className="ticket-category__sale">
                    <Select
                      label="Verkauf"
                      margins={0}
                      value={sale}
                      disabled={!editable}
                      options={SALE_OPTIONS}
                      onChange={(e: any) => changeSale(occassion.id, e.target.value as Sale)}
                    />
                  </div>

                  {sale === 'without' ? (
                    <Typography variant="text-3" color="subdued" className="ticket-category__note">
                      Wird als normale Reservierung ohne Ticket gebucht.
                    </Typography>
                  ) : (
                    category && (
                      <>
                        <div className="ticket-category__price">
                          <TextField
                            type="number"
                            fullWidth
                            label="Ticketpreis"
                            value={category.amount ?? ''}
                            disabled={!editable}
                            inputProps={{ min: 0, step: '0.01' }}
                            onChange={(e) =>
                              changeCategory(occassion.id, { amount: Number(e.currentTarget.value) })
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornmentAtom position="end">
                                  <Typography color="subdued">€</Typography>
                                </InputAdornmentAtom>
                              ),
                            }}
                          />
                        </div>
                        <div className="ticket-category__title">
                          <TextField
                            fullWidth
                            label="Name auf dem Ticket"
                            placeholder={occassion.title}
                            value={category.title?.de ?? ''}
                            disabled={!editable}
                            onChange={(e) => {
                              const de = e.currentTarget.value;
                              changeCategory(occassion.id, {
                                title: de ? { ...(category.title ?? {}), de } : null,
                              });
                            }}
                          />
                        </div>
                      </>
                    )
                  )}

                  {showPicks && category && (
                    <div className="ticket-category__includes">
                      {options.length > 1 && (
                        <PickGroup
                          label="Optionen"
                          items={options}
                          selected={category.optionIds}
                          disabled={!editable}
                          onToggle={(id, checked) =>
                            changeCategory(occassion.id, {
                              optionIds: pickIds(options, category.optionIds, id, checked),
                            })
                          }
                        />
                      )}
                      {addOns.length > 0 && (
                        <PickGroup
                          label="Zusatzleistungen"
                          items={addOns}
                          selected={category.addOnIds}
                          disabled={!editable}
                          onToggle={(id, checked) =>
                            changeCategory(occassion.id, {
                              addOnIds: pickIds(addOns, category.addOnIds, id, checked),
                            })
                          }
                        />
                      )}
                    </div>
                  )}

                  {category && sale !== 'without' && (
                    <div className="ticket-category__vat">
                      <span className="ticket-category__picks-label">MwSt</span>
                      <span className="ticket-category__vat-summary">{vatSummary(category)}</span>
                      <button
                        type="button"
                        className="ticket-category__link"
                        aria-expanded={openVat === occassion.id}
                        onClick={() => setOpenVat((current) => (current === occassion.id ? null : occassion.id))}
                      >
                        {openVat === occassion.id ? 'Schließen' : category.vatComponents?.length ? 'Bearbeiten' : 'Aufteilen'}
                      </button>
                      {openVat === occassion.id && (
                        <div className="ticket-category__vat-editor">
                          <VatSplitEditor
                            idPrefix={`category-${occassion.id}`}
                            grossCents={toVatCents(category.amount ?? 0)}
                            priceHint="Ticketpreis dieser Kategorie; Aufpreise der Optionen kommen hinzu"
                            vatRate={String(defaultRate(category))}
                            vatComponents={category.vatComponents ?? null}
                            country={vatCountry.country}
                            countryLoaded={vatCountry.loaded}
                            storedCountry={vatCountry.stored}
                            restaurantId={restaurantId ?? ''}
                            onChange={({ vatComponents }) =>
                              changeCategory(occassion.id, { vatComponents: vatComponents ?? null })
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <Box className="ticket-categories__summary">
            <Typography variant="text-3" block>
              {priceRange ? `Ticketpreise ${priceRange}` : 'Kein Anlass ist gerade buchbar.'}
            </Typography>
            <Typography variant="text-4" color="subdued" block>
              „Max Tickets available“ gilt für den ganzen Abend über alle Kategorien. Die Plätze je
              Kategorie kommen aus dem Tischplan und werden nicht zusammengezählt.
            </Typography>
          </Box>

          {!!errors.length && (
            <div className="ticket-categories__errors" role="alert">
              <Typography variant="text-3" weight="bold" block>
                Vor dem Speichern noch zu klären
              </Typography>
              <ul>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default TicketCategoriesEditor;
